/**
 * Billing rules as the UI edits them: the kinds, which numbers each needs,
 * and list edits. The backend checks the same things (`prebill/rules.py`).
 */
import type { BillingRule, RuleKind, RuleParams, RuleSeverity } from "../types";

type NumberParam = "max_rate" | "max_hours" | "amount";

export const RULE_KINDS: ReadonlyArray<{
  id: RuleKind;
  label: string;
  needs: NumberParam[];
}> = [
  { id: "block_billing", label: "Block billing", needs: [] },
  { id: "vague_narrative", label: "Vague narrative", needs: [] },
  { id: "clerical", label: "Clerical work billed", needs: [] },
  { id: "internal_conference", label: "Internal conference", needs: [] },
  { id: "rate_cap", label: "Rate cap", needs: ["max_rate"] },
  { id: "hours_threshold", label: "Hours threshold", needs: ["max_hours"] },
  { id: "cost_approval", label: "Cost approval", needs: ["amount"] },
  { id: "custom", label: "Custom", needs: [] }
];

export const PARAM_LABEL: Record<NumberParam, string> = {
  max_rate: "Highest rate ($/h)",
  max_hours: "Hours",
  amount: "Amount ($)"
};

export const CLASSIFICATIONS = [
  { id: "PT", label: "Partner" },
  { id: "AS", label: "Associate" },
  { id: "PL", label: "Paralegal" },
  { id: "LA", label: "Legal assistant" }
] as const;

export const SEVERITY_OPTIONS: ReadonlyArray<{
  id: RuleSeverity;
  label: string;
}> = [
  { id: "error", label: "Blocks approval" },
  { id: "warn", label: "Warning" }
];

export const GUIDELINE_ACCEPT =
  ".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain";
export const MAX_GUIDELINE_BYTES = 15 * 1024 * 1024;

export const kindOf = (kind: RuleKind) =>
  RULE_KINDS.find((k) => k.id === kind) ?? RULE_KINDS[RULE_KINDS.length - 1];

/** "2.1 No block billing" */
export const ruleTitle = (rule: Pick<BillingRule, "section" | "label">) =>
  rule.section ? `${rule.section} ${rule.label}` : rule.label;

/** What's missing before the rule can be saved, or null. */
export const ruleIssue = (rule: {
  kind: RuleKind;
  params: RuleParams;
}): string | null => {
  const missing = kindOf(rule.kind).needs.find(
    (p) => !(Number(rule.params[p]) > 0)
  );
  return missing
    ? `Add the ${PARAM_LABEL[missing].replace(/ \(.*\)$/, "").toLowerCase()}`
    : null;
};

/** Only the params the kind uses, so a changed kind drops stale numbers. */
export const paramsFor = (kind: RuleKind, params: RuleParams): RuleParams => {
  const keep: Array<keyof RuleParams> = [...kindOf(kind).needs];
  if (kind === "rate_cap") keep.push("classification");
  if (kind === "hours_threshold") keep.push("activity", "needs", "per");
  if (kind === "cost_approval") keep.push("expense_kind");
  return Object.fromEntries(
    keep
      .filter((k) => params[k] !== undefined && params[k] !== "")
      .map((k) => [k, params[k]])
  ) as RuleParams;
};

export const withRule = (rules: BillingRule[], rule: BillingRule) =>
  rules.some((r) => r.id === rule.id)
    ? rules.map((r) => (r.id === rule.id ? rule : r))
    : [...rules, rule];

export const withoutRule = (rules: BillingRule[], id: string) =>
  rules.filter((r) => r.id !== id);

export const toggled = (rules: BillingRule[], id: string, on: boolean) =>
  rules.map((r) => (r.id === id ? { ...r, enabled: on } : r));

export const severityText = (severity: RuleSeverity) =>
  severity === "error" ? "Blocks approval" : "Warning";

export const fileProblem = (file: File): string | null => {
  if (!/\.(pdf|docx|txt|md)$/i.test(file.name))
    return "Upload a PDF, Word (.docx) or text file";
  if (file.size > MAX_GUIDELINE_BYTES) return "Keep the file under 15 MB";
  if (!file.size) return "The file is empty";
  return null;
};
